import React from "react";
import { Button, Card, Col, Row, Form, Tab } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { AuthService } from "../../../../authentication/service";
import { ErrorToast, SuccessToast } from "../../../../shared/toasters/toasters";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const schema = yup.object().shape({
    oldPassword: yup.string().required("formErrors.currentPasswordRequired"),
    newPassword: yup.string().required("formErrors.newPasswordRequired"),
    confirmPassword: yup.string()
        .oneOf([yup.ref("formErrors.newPassword"), undefined], "formErrors.passwordMustMatch")
        .required("formErrors.confirmPasswordRequired")
});

interface ChangePasswordFormState {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
}

const ChangePassword: React.FC = () => {
    const { t } = useTranslation(); // Initialize translation hook
    const navigate = useNavigate();
    const { control, handleSubmit, formState: { errors } } = useForm<ChangePasswordFormState>({
        resolver: yupResolver(schema),
        defaultValues: {
            oldPassword: "",
            newPassword: "",
            confirmPassword: ""
        }
    });

    const onSubmit = async (data: ChangePasswordFormState) => {
        try {
            await AuthService.changePassword({
                oldPassword: data.oldPassword,
                newPassword: data.newPassword
            });
            SuccessToast(t('user.passwordChanged'));
            navigate('/profile-overview');
        } catch (error) {
            ErrorToast(error);
        }
    };

    return (
        <React.Fragment>
            <Tab.Pane eventKey="ChangePassword" className="fade" id="user-set-password" role="tabpanel"
                aria-labelledby="user-set-password-tab">
                <Row>
                    <Col sm={12}>
                        <Card>
                            <Card.Header>
                                <h5>{t('user.changePassword')}</h5>
                            </Card.Header>
                            <Card.Body>
                                <Form onSubmit={handleSubmit(onSubmit)}>
                                    <ul className="list-group list-group-flush">
                                        <li className="list-group-item pt-0 px-0">
                                            <Row className="mb-0">
                                                <label className="col-form-label col-md-4 col-sm-12 text-md-end">
                                                    {t('user.currentPassword')} <span className="text-danger">*</span>
                                                </label>
                                                <Col md={8} sm={12}>
                                                    <Controller
                                                        name="oldPassword"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Form.Control
                                                                type="password"
                                                                isInvalid={!!errors.oldPassword}
                                                                placeholder={t('user.enterCurrentPassword')}
                                                                {...field}
                                                            />
                                                        )}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {t(errors.oldPassword?.message as string)}
                                                    </Form.Control.Feedback>
                                                </Col>
                                            </Row>
                                        </li>
                                        <li className="list-group-item px-0">
                                            <Row className="mb-0">
                                                <label className="col-form-label col-md-4 col-sm-12 text-md-end">
                                                    {t('user.newPassword')} <span className="text-danger">*</span>
                                                </label>
                                                <Col md={8} sm={12}>
                                                    <Controller
                                                        name="newPassword"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Form.Control
                                                                type="password"
                                                                isInvalid={!!errors.newPassword}
                                                                placeholder={t('user.enterNewPassword')}
                                                                {...field}
                                                            />
                                                        )}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {t(errors.newPassword?.message as string)}
                                                    </Form.Control.Feedback>
                                                </Col>
                                            </Row>
                                        </li>
                                        <li className="list-group-item pb-0 px-0">
                                            <Row className="mb-0">
                                                <label className="col-form-label col-md-4 col-sm-12 text-md-end">
                                                    {t('user.confirmPassword')} <span className="text-danger">*</span>
                                                </label>
                                                <Col md={8} sm={12}>
                                                    <Controller
                                                        name="confirmPassword"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Form.Control
                                                                type="password"
                                                                isInvalid={!!errors.confirmPassword}
                                                                placeholder={t('user.confirmPasswordPlaceholder')}
                                                                {...field}
                                                            />
                                                        )}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {t(errors.confirmPassword?.message as string)}
                                                    </Form.Control.Feedback>
                                                </Col>
                                            </Row>
                                        </li>
                                    </ul>
                                    <Card.Body className="text-end">
                                        <Button variant="outline-secondary" className="me-2">
                                            {t('user.cancel')}
                                        </Button>
                                        <Button type="submit" variant="primary">
                                            {t('user.changePassword')}
                                        </Button>
                                    </Card.Body>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Tab.Pane>
        </React.Fragment>
    );
};

export default ChangePassword;